import { useMemo } from 'react';
import { useAuthStore } from '@/shared/store/auth';
import type { MemberRole } from '@/shared/types/member';
import type { Permission } from '@/shared/types/permissions';
import { ROLE_PERMISSIONS } from '@/shared/types/permissions';

interface UsePermissionsResult {
  // Current role
  role: MemberRole | null;

  // Role checks
  isOwner: boolean;
  isAdmin: boolean;
  isAnalyst: boolean;
  isViewer: boolean;

  // Permission check functions
  can: (permission: Permission) => boolean;
  canAny: (permissions: Permission[]) => boolean;
  canAll: (permissions: Permission[]) => boolean;

  // Common permission groups for convenience
  canManageTeam: boolean;
  canManageBilling: boolean;
  canManageProjects: boolean;
  canUseChat: boolean;
  canExport: boolean;
  canManageDigests: boolean;
}

/**
 * Hook for checking user permissions based on their role.
 *
 * IMPORTANT: These checks are for UI convenience only.
 * The backend MUST enforce all permissions (OWASP A01:2021).
 *
 * @example
 * const { can, canManageBilling } = usePermissions();
 *
 * // Check specific permission
 * if (can('delete_project')) { ... }
 *
 * // Use convenience properties
 * {canManageBilling && <BillingLink />}
 */
export function usePermissions(): UsePermissionsResult {
  const { user } = useAuthStore();

  const role = (user?.role as MemberRole) ?? null;

  return useMemo(() => {
    // Role checks
    const isOwner = role === 'owner';
    const isAdmin = role === 'admin';
    const isAnalyst = role === 'analyst';
    const isViewer = role === 'viewer';

    // Get permissions for current role
    const permissions = role ? ROLE_PERMISSIONS[role] : [];

    // Permission check functions
    const can = (permission: Permission): boolean => {
      return permissions.includes(permission);
    };

    const canAny = (perms: Permission[]): boolean => {
      return perms.some((p) => permissions.includes(p));
    };

    const canAll = (perms: Permission[]): boolean => {
      return perms.every((p) => permissions.includes(p));
    };

    // Common permission groups
    const canManageTeam = isOwner || isAdmin;
    const canManageBilling = isOwner;
    const canManageProjects = isOwner || isAdmin;
    const canUseChat = isOwner || isAdmin || isAnalyst;
    const canExport = isOwner || isAdmin || isAnalyst;
    const canManageDigests = isOwner || isAdmin || isAnalyst;

    return {
      role,
      isOwner,
      isAdmin,
      isAnalyst,
      isViewer,
      can,
      canAny,
      canAll,
      canManageTeam,
      canManageBilling,
      canManageProjects,
      canUseChat,
      canExport,
      canManageDigests,
    };
  }, [role]);
}
